import { validateAwsRoutingCriteria } from './awsRoutingCriteriaValidator';
import { validateRoutingPolicy } from './routingPolicyValidator';

const COMPARISON_OPERATOR = 'NumberGreaterOrEqualTo';

const toAwsName = (name) => {
  const words = String(name || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join('');
};

const getAttribute = (attributeId, attributes) => (
  attributes.find((attribute) => attribute.attributeId === attributeId)
);

const getAllowedValue = (attribute, value) => (
  attribute.allowedValues.find((allowedValue) => (
    (allowedValue && typeof allowedValue === 'object' ? allowedValue.value : allowedValue) === value
  ))
);

const resolveName = (attribute) => toAwsName(attribute.attributeName || attribute.displayName || attribute.attributeId);

const resolveValue = (attribute, value) => {
  const allowedValue = getAllowedValue(attribute, value);
  return allowedValue && typeof allowedValue === 'object'
    ? allowedValue.displayName || allowedValue.value
    : allowedValue;
};

export class RoutingPolicyCompilationError extends Error {
  constructor(message, errors) {
    super(message);
    this.name = 'RoutingPolicyCompilationError';
    this.errors = errors;
  }
}

export const resolveAttributes = (attributes = []) => (
  attributes.flatMap((attribute) => (
    (attribute.allowedValues || []).map((allowedValue) => ({
      Name: resolveName(attribute),
      Value: typeof allowedValue === 'object'
        ? allowedValue.displayName || allowedValue.value
        : allowedValue
    }))
  ))
);

export const compileAttributeCondition = (condition, attributes = []) => {
  const attribute = getAttribute(condition.attributeId, attributes);
  if (!attribute) {
    throw new RoutingPolicyCompilationError(`Attribute '${condition.attributeId}' does not exist.`, [{
      code: 'ATTRIBUTE_NOT_FOUND',
      message: `Attribute '${condition.attributeId}' does not exist.`,
      path: 'attributeId'
    }]);
  }

  return {
    AttributeCondition: {
      Name: resolveName(attribute),
      Value: resolveValue(attribute, condition.value),
      ProficiencyLevel: Number(condition.proficiency),
      ComparisonOperator: COMPARISON_OPERATOR
    }
  };
};

export const compileAndExpression = (criteria, attributes = []) => ({
  AndExpression: criteria.conditions.map((condition) => compileAttributeCondition(condition, attributes))
});

const compileExpression = (expression, attributes) => (
  Array.isArray(expression?.conditions)
    ? compileAndExpression(expression, attributes)
    : compileAttributeCondition(expression, attributes)
);

export const compileOrExpression = (criteria, attributes = []) => ({
  OrExpression: criteria.conditions.map((expression) => compileExpression(expression, attributes))
});

export const compileCriteriaExpression = (criteria, attributes = []) => (
  criteria.logic === 'OR'
    ? compileOrExpression(criteria, attributes)
    : compileAndExpression(criteria, attributes)
);

export const compileExpiration = (timeoutSeconds) => (
  timeoutSeconds === null || timeoutSeconds === undefined || timeoutSeconds === ''
    ? {}
    : { Expiry: { DurationInSeconds: Number(timeoutSeconds) } }
);

export const compilePolicyCriteria = (policy, routingGroups = [], attributes = []) => {
  const policyValidation = validateRoutingPolicy(policy, routingGroups, attributes);
  if (!policyValidation.valid) {
    throw new RoutingPolicyCompilationError('Routing policy validation failed.', policyValidation.errors);
  }

  const groupsById = new Map(routingGroups.map((group) => [group.routingGroupId, group]));
  const compiled = {
    RoutingCriteria: {
      Steps: policy.routingSteps.map((step) => ({
        Expression: compileCriteriaExpression(groupsById.get(step.routingGroupId).criteria, attributes),
        ...compileExpiration(step.timeoutSeconds)
      }))
    }
  };

  const awsValidation = validateAwsRoutingCriteria(compiled);
  if (!awsValidation.valid) {
    throw new RoutingPolicyCompilationError('Compiled AWS routing criteria validation failed.', awsValidation.errors);
  }
  return compiled;
};

export default compilePolicyCriteria;